import React, { useState, useMemo, useCallback, useRef, useContext, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { connect } from 'react-redux';

import { makeGetDoc } from 'selectors/docs';
import { makeGetNotificationsByAssociationId } from 'selectors/notifications';

import { fetchDoc, updateDoc } from 'actions/docs';
import { removeMessages } from 'actions/messages';

import { createAlert } from 'services/alert-service';
import { logError } from 'shared-services/logger-service';
import { useCurrentUserId } from 'shared-hooks/user';
import { getDocLastUpdate } from 'utils/doc';

import { SocketContext } from 'context/SocketContext';

import Spinner from 'components/spinner';
import DocEditor from 'components/doc-editor';
import NotificationsReader from 'components/NotificationsReader';
import MultiLineInput from 'components/MultiLineInput';
import DateTime from 'components/DateTime';

const NiftyDoc = ({ id, projectId, initialName, canEdit, permission, noLiveEdit, docMembers, preview, isPublic, initialThreadId, tocContainer, doc, comments, notifications, onScroll, onChange, onNameChange, onCollapsedChange, onLoad, provideController }) => {
    const socketCtx = useContext(SocketContext);
    const [name, setName] = useState(initialName || doc?.name);
    const [reloading, setReloading] = useState(false);
    const holderElement = useRef(null);
    const docNameState = useRef(null);
    const docCtrl = useRef(null);
    const currentConnectedDocId = useRef(null);
    const saveErrorAlert = useRef(null);
    const { t } = useTranslation(['doc', 'date']);
    const currentUserId = useCurrentUserId();

    const lastEditedMessage = useMemo(() => {
        if (!doc?.members) {
            return null;
        }

        const lastEdited = getDocLastUpdate(doc.members, doc.author, doc.created_at);

        if (new Date() - new Date(lastEdited.date) > 60000) {
            return <Trans
                i18nKey="date:Updated on"
                components={{
                    DateTime: <DateTime format="fullDateTime" />
                }}
                values={{
                    date: lastEdited.date
                }}
            />
        } else {
            return t('date:Updated now');
        }
    }, [doc, t]);

    const attemptFetch = useCallback(force => {
        if (!id || doc?.loading) {
            return;
        }

        if (!doc || !doc?.content || force) {
            fetchDoc(id, !Boolean(id));
        }
    }, [id, doc]);

    const joinDoc = useCallback(() => {
        if (isPublic) {
            return;
        }

        if (socketCtx?.connectToRoom) {
            socketCtx.connectToRoom('document', id);
            currentConnectedDocId.current = id;
        } else {
            logError('nifty-doc', new Error(`${socketCtx ? 'context.connectToRoom' : 'context'} isn't available in doc`));
        }
    }, [id, isPublic, socketCtx]);

    const leaveDoc = useCallback(() => {
        if (!currentConnectedDocId.current) {
            return;
        }

        socketCtx?.disconnectFromRoom && socketCtx.disconnectFromRoom('document', currentConnectedDocId.current);
    }, [id, currentConnectedDocId, socketCtx]);

    const handleNameBlur = useCallback(nextName => {
        if (!nextName) {
            nextName = 'Untitled';
        }

        if (nextName !== doc?.name && id) {
            updateDoc(id, { name: nextName });
        }

        if (nextName !== name) {
            setName(nextName);
            docNameState.current = nextName;
        }
    }, [id, doc?.name, name, setName]);

    const handleNameChange = useCallback(nextName => {
        setName(nextName || 'Untitled');
        docNameState.current = nextName || 'Untitled';
        onChange && onChange();
    }, [setName, onChange]);

    const handleNameKeyDown = useCallback(event => {
        if (event.which === 13) {
            event.preventDefault();

            setTimeout(() => {
                docCtrl.current?.focus(true);
            });
        }
    }, [docCtrl.current]);


    const handleCommentsThreadRemove = threadId => {
        removeMessages(threadId);
    }

    const handleDocReload = useCallback(() => {
        leaveDoc();
        setReloading(true);

        setTimeout(() => {
            joinDoc();
            setReloading(false)
        }, 500);
    }, [leaveDoc, joinDoc, setReloading]);

    useEffect(() => {
        attemptFetch();
    }, [attemptFetch]);

    useEffect(() => {
        leaveDoc();

        if (id) {
            joinDoc();
        }

        attemptFetch(true);

        onScroll && onScroll();

        const ctrl = {
            isPopulated: () => {
                return docCtrl.current?.isPopulated();
            },
            getContent: () => {
                return docCtrl.current?.getValue();
            },
            getName: () => {
                return docNameState.current || doc?.name;
            },
            getHTML: () => {
                return docCtrl.current?.getHTML();
            },
            getEditor: () => {
                return docCtrl.current?.getEditor();
            },
            updateScrollPosition: scrollPosition => {
                return docCtrl.current?.updateScrollPosition(scrollPosition);
            },
            toggleType: (type, data) => {
                return docCtrl.current?.toggleType(type, data);
            }
        };

        if (provideController) {
            if ('current' in provideController) {
                provideController.current = ctrl;
            } else {
                provideController && provideController(ctrl);
            }
        }

        return () => {
            leaveDoc();

            if (id) {
                updateDoc(id, { content: '' }, true);
            }

            if (provideController) {
                if ('current' in provideController) {
                    provideController.current = null;
                } else {
                    provideController && provideController(null);
                }
            }
        }
    }, [id]);

    useEffect(() => {
        if (doc?.name && doc.name !== name) {
            setName(doc.name);
            docNameState.current = doc.name;
        }
    }, [doc?.name]);

    useEffect(() => {
        onNameChange(name);
    }, [name, onNameChange]);

    useEffect(() => {
        if (doc?.content) {
            onLoad && onLoad();
        }
    }, [doc?.content]);

    useEffect(() => {
        if (!doc) {
            return;
        }

        if (doc.applyError) {
            saveErrorAlert.current = createAlert('default', t('errors.corrupted'), {
                duration: 0,
                noClose: true
            })

            attemptFetch(true);
        } else if (saveErrorAlert.current) {
            saveErrorAlert.current.setContent(t('errors.restored')).setType('success').hide(2000);
            saveErrorAlert.current = null;
        }
    }, [doc?.applyError]);

    return (
        <div className="document-editor-canvas-holder" ref={holderElement}>
            <NotificationsReader notifications={notifications.filter(notification => !notification.comment_id).map(({ id }) => id)} />

            <div className="document-editor-canvas-scroll-pane" onScroll={onScroll}>
                <div className="document-editor-canvas">
                    {
                        (preview && !canEdit) ? null : (
                            <div className="document-editor-title">
                                <MultiLineInput
                                    className="doc-field-input"
                                    canEdit={canEdit}
                                    value={!isPublic && doc?.name === 'Untitled' ? '' : doc?.name}
                                    placeholder="Give it a name"
                                    autoFocus={true}
                                    onChange={handleNameChange}
                                    onBlur={handleNameBlur}
                                    onKeyDown={handleNameKeyDown}
                                />
                            </div>
                        )
                    }

                    <div className={'document-editor-content-holder'}>
                        <Spinner />

                        <div className="document-editor-content">
                            {
                                !reloading && (!id || doc?.content) ? (
                                    <DocEditor
                                        key={id}
                                        id={id}
                                        projectId={projectId}
                                        noLiveEdit={noLiveEdit}
                                        value={doc?.content}
                                        currentUserId={currentUserId}
                                        name={doc?.name}
                                        threads={comments}
                                        onChange={onChange}
                                        permission={permission}
                                        notifications={notifications}
                                        readOnly={doc?.applyError}
                                        showToc={true}
                                        isPublic={isPublic}
                                        useUrlsForFiles={isPublic}
                                        docMembers={docMembers}
                                        initialThreadId={initialThreadId}
                                        tocContainer={holderElement.current}
                                        onCommentsThreadRemove={handleCommentsThreadRemove}
                                        onReloadDoc={handleDocReload}
                                        onCollapsedChange={onCollapsedChange}
                                        provideController={docCtrl}
                                    />
                                ) : null
                            }
                        </div>
                    </div>
                </div>
            </div>

            {
                lastEditedMessage ? (
                    <div className="document-last-edited">
                        <i className="icon material-icons">update</i> { lastEditedMessage }
                    </div>
                ) : null
            }
        </div>
    );
}

const makeMapStateToProps = () => {
    const getNotificationsByAssociationId = makeGetNotificationsByAssociationId();
    const getDoc = makeGetDoc();

    return ({ docs, messages, notifications }, ownProps) => {
        const doc = ownProps.id && getDoc(docs.items, ownProps.id);

        return {
            doc,
            // TODO: Impvove
            comments: messages.items,
            notifications: getNotificationsByAssociationId(notifications.items, ownProps.id)
        }
    };    
}

export default connect(makeMapStateToProps)(NiftyDoc);
